import {
  type FromExtension,
  type ToExtension,
  parseFromExtension,
  toExtension,
} from "./protocol";

/** ページの窓口。送信元とオリジンの確認は、受け取る側の実装で済ませてから渡す */
export type BridgePort = {
  listen: (onMessage: (data: unknown) => void) => () => void;
  post: (message: Record<string, unknown>) => void;
};

export type BridgeTimer = {
  schedule: (callback: () => void, delayMs: number) => () => void;
};

export type AcquireOutcome =
  | { kind: "granted"; leaseId: string }
  | { kind: "refused" }
  | { kind: "unavailable" };

/** 時間切れの後に届いた取得の返事。使えないという返事は、遅れて届いても何もしない */
export type LateAcquireReply =
  | { kind: "granted"; leaseId: string }
  | { kind: "refused" };

export type HeartbeatOutcome = "held" | "lost" | "unavailable";

export type EhrExtensionBridge = {
  /** 拡張機能の返事を受け付け始める。何度呼んでもよい */
  start: () => void;
  /** 拡張機能の content script がいると知らされているか */
  present: () => boolean;
  /**
   * 録音リースを求める。拡張機能がいなければ待たずに使えないと返す。時間切れの
   * 後に返事が届いたら `onLate` に渡す
   */
  acquire: (
    onLate: (reply: LateAcquireReply) => void,
  ) => Promise<AcquireOutcome>;
  heartbeat: (leaseId: string) => Promise<HeartbeatOutcome>;
  release: (leaseId: string) => void;
  /** 拡張機能に問われたときに、録音中か開始の準備中かを答える関数を決める */
  setBusy: (busy: () => boolean) => void;
};

/**
 * 拡張機能がいるのに返事が無いときに待つ上限。待つのは service worker が止まって
 * いて起き直す間で、通常は数百 ms で返る。過ぎたら拡張機能に問わずに録音を始める
 */
export const ACQUIRE_TIMEOUT_MS = 2_000;
export const HEARTBEAT_TIMEOUT_MS = 2_000;
/** 時間切れの後に遅れて届く取得の返事を受け付ける上限 */
export const LATE_REPLY_WINDOW_MS = 60_000;

type Pending = {
  receive: (message: FromExtension) => void;
  /** 拡張機能がいなくなった。返事は届かない */
  abandon: () => void;
};

export const createEhrExtensionBridge = (
  port: BridgePort,
  timer: BridgeTimer,
  newId: () => string,
): EhrExtensionBridge => {
  const state: {
    started: boolean;
    instance: string | null;
    busy: () => boolean;
  } = { started: false, instance: null, busy: () => false };
  const pending = new Map<string, Pending>();

  /** 送れたか。送れなければ返事は来ないため、待たずに使えないものとする */
  const post = (message: ToExtension): boolean => {
    try {
      port.post(toExtension(message));
      return true;
    } catch {
      return false;
    }
  };

  const answerHandshake = (requestId: string) => {
    const busy = (() => {
      try {
        return state.busy();
      } catch {
        return false;
      }
    })();
    post({ type: "handshake-reply", requestId, busy });
  };

  const receive = (message: FromExtension) => {
    switch (message.type) {
      case "present":
        state.instance = message.instance;
        return;
      case "absent":
        // 拡張機能が更新されると、差し込み直された新しい content script が先に
        // present を送ることがある。古い content script の absent で消さない
        if (state.instance !== message.instance) return;
        state.instance = null;
        for (const entry of [...pending.values()]) entry.abandon();
        return;
      case "handshake":
        answerHandshake(message.requestId);
        return;
      default:
        pending.get(message.requestId)?.receive(message);
    }
  };

  const request = <T>(options: {
    message: (requestId: string) => ToExtension;
    timeoutMs: number;
    unavailable: T;
    read: (message: FromExtension) => T | null;
    onLate?: (value: T) => void;
  }): Promise<T> =>
    new Promise((resolve) => {
      const requestId = newId();
      const settled = { value: false };
      const finish = (value: T) => {
        if (settled.value) return;
        settled.value = true;
        resolve(value);
      };
      const forget = () => {
        pending.delete(requestId);
      };
      const cancelTimeout = timer.schedule(() => {
        finish(options.unavailable);
        if (options.onLate === undefined) {
          forget();
          return;
        }
        timer.schedule(forget, LATE_REPLY_WINDOW_MS);
      }, options.timeoutMs);
      pending.set(requestId, {
        receive: (message) => {
          const value = options.read(message);
          if (value === null) return;
          forget();
          if (!settled.value) {
            cancelTimeout();
            finish(value);
            return;
          }
          options.onLate?.(value);
        },
        abandon: () => {
          forget();
          cancelTimeout();
          finish(options.unavailable);
        },
      });
      if (!post(options.message(requestId))) {
        pending.get(requestId)?.abandon();
      }
    });

  return {
    start: () => {
      if (state.started) return;
      try {
        port.listen((data) => {
          const message = parseFromExtension(data);
          if (message !== null) receive(message);
        });
      } catch {
        // 次の開始の操作でもう一度受け付けを始める
        return;
      }
      state.started = true;
      post({ type: "hello" });
    },
    present: () => state.instance !== null,
    acquire: (onLate) => {
      if (state.instance === null) {
        return Promise.resolve({ kind: "unavailable" });
      }
      return request<AcquireOutcome>({
        message: (requestId) => ({ type: "acquire", requestId }),
        timeoutMs: ACQUIRE_TIMEOUT_MS,
        unavailable: { kind: "unavailable" },
        read: (message) => {
          switch (message.type) {
            case "acquire-granted":
              return { kind: "granted", leaseId: message.leaseId };
            case "acquire-refused":
              return { kind: "refused" };
            case "acquire-unavailable":
              return { kind: "unavailable" };
            default:
              return null;
          }
        },
        onLate: (value) => {
          if (value.kind !== "unavailable") onLate(value);
        },
      });
    },
    heartbeat: (leaseId) => {
      if (state.instance === null) {
        return Promise.resolve("unavailable");
      }
      return request<HeartbeatOutcome>({
        message: (requestId) => ({ type: "heartbeat", requestId, leaseId }),
        timeoutMs: HEARTBEAT_TIMEOUT_MS,
        unavailable: "unavailable",
        read: (message) =>
          message.type === "heartbeat-reply" ? message.result : null,
      });
    },
    release: (leaseId) => {
      if (state.instance !== null) post({ type: "release", leaseId });
    },
    setBusy: (busy) => {
      state.busy = busy;
    },
  };
};

/** 同じ文書の medimo Web からのメッセージと、同じ文書の拡張機能からのメッセージだけを受け付ける */
export const windowBridgePort = (target: Window): BridgePort => ({
  listen: (onMessage) => {
    const listener = (event: MessageEvent) => {
      if (event.source !== target || event.origin !== target.location.origin) {
        return;
      }
      onMessage(event.data);
    };
    target.addEventListener("message", listener);
    return () => target.removeEventListener("message", listener);
  },
  post: (message) => {
    target.postMessage(message, target.location.origin);
  },
});

export const browserTimer: BridgeTimer = {
  schedule: (callback, delayMs) => {
    const id = setTimeout(callback, delayMs);
    return () => clearTimeout(id);
  },
};

const singleton: { bridge: EhrExtensionBridge | null } = { bridge: null };

/** ブラウザの中でだけ作る。サーバーでの描画では null */
export const getEhrExtensionBridge = (): EhrExtensionBridge | null => {
  if (typeof window === "undefined") return null;
  if (singleton.bridge === null) {
    singleton.bridge = createEhrExtensionBridge(
      windowBridgePort(window),
      browserTimer,
      () => crypto.randomUUID(),
    );
  }
  return singleton.bridge;
};
